import { FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Dispatch, SetStateAction } from 'react';
import { BulkImportType } from '../../../../../types';
import { BulkOpsFormValues } from './create-bulk-op-dialog';
import SharedFilePickerOld from '../../../../shared/shared-file-picker-old';
import SharedFilePicker from '../../../../shared/shared-file-picker';
import SharedButton from '../../../../shared/buttons/shared-button';
import { FormSectionHeader } from '../../../../shared/form-section-header';
import { useGenerateTemplate } from './use-generate-template';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { isFeatureEnabled } from '../../../../utils/feature-flags';
import OptionCard from './option-card';
import SelectedFileRow from './selected-file-row';

import Alert from '@mui/material/Alert';
import Grow from '@mui/material/Grow';
import Stack from '@mui/material/Stack';

type SpreadsheetOption = 'template' | 'existing';

interface StepSpreadsheetTemplateProps {
  setValue: UseFormSetValue<BulkOpsFormValues>;
  watch: UseFormWatch<BulkOpsFormValues>;
  spreadSheetName: string;
  setSpreadSheetName: Dispatch<SetStateAction<string>>;
  selectedBulkImportType: BulkImportType | null;
  selectedOption: SpreadsheetOption | null;
  setSelectedOption: Dispatch<SetStateAction<SpreadsheetOption | null>>;
  errors: FieldErrors<BulkOpsFormValues>;
  onContinue: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export default function StepSpreadsheetTemplate({
  setValue,
  watch,
  spreadSheetName,
  setSpreadSheetName,
  selectedBulkImportType,
  selectedOption,
  setSelectedOption,
  errors,
  onContinue,
}: StepSpreadsheetTemplateProps) {
  const spreadsheetId = watch('spreadsheetId') || '';
  const { features } = useUserSettings();
  const FilePicker = isFeatureEnabled('drivePickerUIReact', features)
    ? SharedFilePicker
    : SharedFilePickerOld;
  const { handleUseTemplate, isGeneratingTemplate } = useGenerateTemplate(
    setValue,
    setSpreadSheetName,
    selectedBulkImportType
  );

  // Only one file can be attached, so only one card shows it.
  const attachedTo = spreadsheetId ? selectedOption : null;

  const handleFileRemoved = () => {
    setValue('spreadsheetId', '');
    setSpreadSheetName('');
  };

  const fileRow = (
    <SelectedFileRow
      name={spreadSheetName}
      onRemove={handleFileRemoved}
      action={
        <SharedButton variant="primary" onClick={onContinue}>
          Continue
        </SharedButton>
      }
    />
  );

  return (
    <Stack gap={3} width={'100%'}>
      <FormSectionHeader
        descriptionVariant="body2"
        description="This bulk operation runs from a Google Sheet with the required column headers. Our template is the easiest way to get them right."
      >
        Select Spreadsheet
      </FormSectionHeader>
      <Stack
        flexDirection={'row'}
        gap={4}
        // Design: equal-height cards until a file is attached to one of them.
        alignItems={attachedTo ? 'flex-start' : 'stretch'}
        width={'100%'}
      >
        <OptionCard
          title="Start with our template"
          selected={attachedTo === 'template'}
          actions={
            <Stack gap={3} sx={{ width: '100%' }}>
              <SharedButton
                variant={attachedTo === 'template' ? 'secondary' : 'primary'}
                onClick={async () => {
                  await handleUseTemplate();
                  setSelectedOption('template');
                }}
                disabled={!selectedBulkImportType}
                loading={isGeneratingTemplate}
                sx={{ alignSelf: 'flex-start' }}
              >
                {attachedTo === 'template' ? 'New Template' : 'Use Template'}
              </SharedButton>
              {attachedTo === 'template' && fileRow}
            </Stack>
          }
        >
          <p>
            We’ll create a Google Sheet with the right column headers in your My
            Drive and select it for this bulk operation.
          </p>
        </OptionCard>
        <OptionCard
          title="I already have a spreadsheet"
          selected={attachedTo === 'existing'}
          actions={
            <Stack gap={3} sx={{ width: '100%' }}>
              <FilePicker
                pickerType="spreadsheet"
                selectedFileId={spreadsheetId}
                selectedFileName={spreadSheetName}
                onFileSelected={({ id, name }) => {
                  setSelectedOption('existing');
                  setValue('spreadsheetId', id);
                  setSpreadSheetName(name);
                }}
                onFileRemoved={handleFileRemoved}
                includeSharedDrives
                customTrigger={(openPicker) => (
                  <SharedButton
                    variant={
                      attachedTo === 'existing' ? 'secondary' : 'primary'
                    }
                    onClick={openPicker}
                  >
                    {attachedTo === 'existing' ? 'Change File' : 'Select File'}
                  </SharedButton>
                )}
              />
              {attachedTo === 'existing' && fileRow}
            </Stack>
          }
        >
          <p>
            Select a Google Sheet that already has the required column headers.
          </p>
        </OptionCard>
      </Stack>
      {errors.spreadsheetId && (
        <Grow in={true}>
          <Alert severity="error" sx={{ width: 'fit-content' }}>
            {errors.spreadsheetId.message}
          </Alert>
        </Grow>
      )}
    </Stack>
  );
}
